import { useCallback, useEffect, useState } from "react";
import { Percent, Plus, Calculator, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { PageHeader, EmptyState, StatCard } from "@/components/Shared";
import { COUNTRIES, countryName } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BLANK_RATE = { dest_country: "US", origin_country: "any", category: "steel", hs_code: "", duty_pct: 0, vat_pct: 0, flat_fee: 0, note: "" };
const BLANK_LINE = { description: "", category: "steel", hs_code: "", value: 0, quantity: 1 };

export default function Tariffs() {
  const { user } = useAuth();
  const { base_currency: baseCurrency, home_country: homeCountry } = useCurrency();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("calculator");
  const [meta, setMeta] = useState(null);
  const [rates, setRates] = useState([]);
  const [filter, setFilter] = useState({ dest: "all", category: "all" });
  const [rate, setRate] = useState(BLANK_RATE);
  const [dialog, setDialog] = useState(false);
  const [calc, setCalc] = useState({ dest_country: homeCountry || "US", origin_country: "CN", freight_cost: 0, insurance_cost: 0, lines: [{ ...BLANK_LINE }] });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const money = (v) => `${baseCurrency || "USD"} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([
        api.get("/tariffs/meta"),
        api.get(`/tariffs?dest_country=${filter.dest}&category=${filter.category}`),
      ]);
      setMeta(m.data);
      setRates(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const labels = meta?.category_labels || {};
  const categories = meta?.categories || [];

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/tariffs", {
        ...rate,
        origin_country: rate.origin_country === "any" ? null : rate.origin_country,
        hs_code: rate.hs_code || null,
        duty_pct: Number(rate.duty_pct) || 0,
        vat_pct: Number(rate.vat_pct) || 0,
        flat_fee: Number(rate.flat_fee) || 0,
      });
      toast.success("Rate added");
      setDialog(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const runCalc = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tariffs/quote", {
        dest_country: calc.dest_country,
        origin_country: calc.origin_country,
        freight_cost: Number(calc.freight_cost) || 0,
        insurance_cost: Number(calc.insurance_cost) || 0,
        currency: baseCurrency || "usd",
        lines: calc.lines.map((l) => ({
          description: l.description || null, category: l.category,
          hs_code: l.hs_code || null, value: Number(l.value) || 0,
          quantity: Number(l.quantity) || 1,
        })),
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const patchLine = (i, patch) => setCalc((c) => ({ ...c, lines: c.lines.map((l, k) => (k === i ? { ...l, ...patch } : l)) }));

  return (
    <div data-testid="tariffs-page">
      <PageHeader
        overline="Trade"
        title="International tariffs"
        description="Import duty and VAT by product and trade lane, and what a shipment really costs once duty, freight and tax land."
        action={isAdmin && (
          <Button data-testid="add-rate-button" onClick={() => { setRate({ ...BLANK_RATE, dest_country: homeCountry || "US" }); setDialog(true); }}>
            <Plus size={16} className="mr-1.5" /> Add a rate
          </Button>
        )}
      />

      <div className="mb-5 grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3" data-testid="tariff-stats">
        <StatCard label="Rates on file" value={rates.length} sub="duty and VAT by lane" icon={Percent} tone="primary" testid="stat-rates" />
        <StatCard label="Destinations" value={new Set(rates.map((r) => r.dest_country)).size} sub="countries you import into" icon={Percent} testid="stat-destinations" />
        <StatCard label="Home country" value={homeCountry || "—"} sub="duty applies on imports into here" icon={Percent} testid="stat-home-country" />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          <TabsTrigger value="calculator" data-testid="tariff-tab-calculator" className="rounded-none text-xs uppercase tracking-wider">Landed cost</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tariff-tab-rates" className="rounded-none text-xs uppercase tracking-wider">Rate book ({rates.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "calculator" && (
        <div data-testid="tariff-calculator">
          <div className="grid gap-3 border border-border bg-white p-5 sm:grid-cols-4">
            <div>
              <Label>Importing into</Label>
              <Select value={calc.dest_country} onValueChange={(v) => setCalc({ ...calc, dest_country: v })}>
                <SelectTrigger data-testid="calc-dest-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Coming from</Label>
              <Select value={calc.origin_country} onValueChange={(v) => setCalc({ ...calc, origin_country: v })}>
                <SelectTrigger data-testid="calc-origin-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Freight cost</Label><Input data-testid="calc-freight-input" type="number" min="0" value={calc.freight_cost} onChange={(e) => setCalc({ ...calc, freight_cost: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Insurance cost</Label><Input data-testid="calc-insurance-input" type="number" min="0" value={calc.insurance_cost} onChange={(e) => setCalc({ ...calc, insurance_cost: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>

          <div className="mt-3 space-y-2" data-testid="calc-lines">
            {calc.lines.map((l, i) => (
              <div key={i} className="grid gap-2 border border-border bg-white p-3 sm:grid-cols-6">
                <Input data-testid={`calc-line-desc-${i}`} placeholder="What is it?" value={l.description} onChange={(e) => patchLine(i, { description: e.target.value })} className="rounded-none border-slate-300 sm:col-span-2" />
                <Select value={l.category} onValueChange={(v) => patchLine(i, { category: v })}>
                  <SelectTrigger data-testid={`calc-line-category-${i}`} className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{categories.map((c) => <SelectItem key={c} value={c}>{labels[c] || c}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid={`calc-line-hs-${i}`} placeholder="HS code (optional)" value={l.hs_code} onChange={(e) => patchLine(i, { hs_code: e.target.value })} className="rounded-none border-slate-300" />
                <Input data-testid={`calc-line-value-${i}`} type="number" min="0" placeholder="Goods value" value={l.value} onChange={(e) => patchLine(i, { value: e.target.value })} className="rounded-none border-slate-300" />
                <div className="flex gap-2">
                  <Input data-testid={`calc-line-qty-${i}`} type="number" min="1" placeholder="Qty" value={l.quantity} onChange={(e) => patchLine(i, { quantity: e.target.value })} className="rounded-none border-slate-300" />
                  {calc.lines.length > 1 && (
                    <Button size="icon" variant="ghost" data-testid={`calc-line-remove-${i}`} onClick={() => setCalc((c) => ({ ...c, lines: c.lines.filter((_, k) => k !== i) }))}>
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" data-testid="calc-add-line" onClick={() => setCalc((c) => ({ ...c, lines: [...c.lines, { ...BLANK_LINE }] }))}>Add a line</Button>
            <Button size="sm" data-testid="calc-run-button" onClick={runCalc} disabled={busy}>
              <Calculator size={15} className="mr-1.5" /> {busy ? "Working it out…" : "Work out landed cost"}
            </Button>
          </div>

          {result && (
            <div className="mt-5" data-testid="calc-results">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {[["Goods", result.goods_total], ["Duty", result.duty_total], ["VAT / GST", result.vat_total], ["Freight", result.freight_total], ["Landed total", result.landed_total]].map(([label, v], i) => (
                  <div key={label} className={`border bg-white p-3 ${i === 4 ? "border-[#002FA7]" : "border-border"}`}>
                    <div className="overline text-[0.6rem] text-slate-500">{label}</div>
                    <div className="font-display mt-1 text-base font-bold text-[#0F172A]" data-testid={`calc-total-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{money(v)}</div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500" data-testid="calc-note">
                Effective duty {result.duty_effective_pct}% · {result.note}
                {result.unmatched_lines > 0 && ` · ${result.unmatched_lines} line(s) had no rate on file and were treated as duty free.`}
              </p>
              <div className="mt-3 overflow-x-auto border border-border bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>{["Line", "Category", "HS", "Value", "Duty %", "Duty", "VAT", "Freight share", "Landed", "Per unit"].map((h) => (
                      <th key={h} className="border-b border-border px-2 py-2 text-left font-semibold text-[#0F172A]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {result.lines.map((l, i) => (
                      <tr key={i} data-testid={`calc-line-result-${i}`} className="border-b border-border last:border-0">
                        <td className="px-2 py-2 text-[#0F172A]">{l.description || "—"}</td>
                        <td className="px-2 py-2 text-slate-600">{labels[l.category] || l.category}</td>
                        <td className="px-2 py-2 font-mono text-slate-500">{l.hs_code || "—"}</td>
                        <td className="px-2 py-2 font-mono text-slate-600">{money(l.value)}</td>
                        <td className="px-2 py-2 font-mono text-slate-600">{l.duty_pct}%</td>
                        <td className="px-2 py-2 font-mono text-slate-600">{money(l.duty)}</td>
                        <td className="px-2 py-2 font-mono text-slate-600">{money(l.vat)}</td>
                        <td className="px-2 py-2 font-mono text-slate-600">{money(l.freight_share)}</td>
                        <td className="px-2 py-2 font-mono font-bold text-[#0F172A]">{money(l.landed_total)}</td>
                        <td className="px-2 py-2 font-mono text-slate-600">{money(l.landed_per_unit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "rates" && (
        <div data-testid="tariff-rates">
          <div className="mb-3 grid gap-2 sm:grid-cols-3">
            <Select value={filter.dest} onValueChange={(v) => setFilter({ ...filter, dest: v })}>
              <SelectTrigger data-testid="rate-filter-dest" className="rounded-none border-slate-300"><SelectValue placeholder="Destination" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">Every destination</SelectItem>
                {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filter.category} onValueChange={(v) => setFilter({ ...filter, category: v })}>
              <SelectTrigger data-testid="rate-filter-category" className="rounded-none border-slate-300"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">Every category</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{labels[c] || c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {rates.length === 0 ? (
            <EmptyState testid="no-rates" title="No rates on file" description="Add a duty rate for the lanes and products you import." />
          ) : (
            <>
            <div className="grid gap-2 md:hidden" data-testid="rate-cards">
              {rates.map((r) => (
                <div key={r.id} data-testid={`rate-card-${r.id}`} className="border border-border bg-white p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">
                        {countryName(r.dest_country) || r.dest_country}
                        <span className="font-normal text-slate-500"> ← {r.origin_country ? countryName(r.origin_country) : "Any origin"}</span>
                      </div>
                      <div className="text-xs text-slate-600">
                        {r.category ? (labels[r.category] || r.category) : "Any category"}
                        {r.hs_code ? ` · HS ${r.hs_code}` : ""}
                      </div>
                    </div>
                    {isAdmin && (
                      <button type="button" data-testid={`delete-rate-card-${r.id}`} onClick={async () => {
                        if (!window.confirm(`Remove the ${labels[r.category] || r.category || `HS ${r.hs_code}`} rate for ${countryName(r.dest_country) || r.dest_country}?`)) return;
                        try { await api.delete(`/tariffs/${r.id}`); toast.success("Rate removed"); await load(); }
                        catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                      }} className="shrink-0 text-xs font-semibold text-[#B3241B]">Remove</button>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 border-t border-border pt-2 text-xs">
                    <div><div className="overline text-[0.55rem] text-slate-500">Duty</div><div className="font-mono font-bold text-[#0F172A]">{r.duty_pct}%</div></div>
                    <div><div className="overline text-[0.55rem] text-slate-500">VAT / GST</div><div className="font-mono text-slate-700">{r.vat_pct}%</div></div>
                    <div><div className="overline text-[0.55rem] text-slate-500">Flat fee</div><div className="font-mono text-slate-700">{r.flat_fee || 0}</div></div>
                  </div>
                  {r.note && <p className="mt-2 text-xs text-slate-500">{r.note}</p>}
                </div>
              ))}
            </div>
            <div className="hidden overflow-x-auto border border-border bg-white md:block">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>{["Into", "From", "Category", "HS code", "Duty", "VAT / GST", "Flat fee", "Note", ""].map((h) => (
                    <th key={h} className="border-b border-border px-3 py-2 text-left font-semibold text-[#0F172A]">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {rates.map((r) => (
                    <tr key={r.id} data-testid={`rate-${r.id}`} className="border-b border-border last:border-0">
                      <td className="px-3 py-2 text-[#0F172A]">{countryName(r.dest_country) || r.dest_country}</td>
                      <td className="px-3 py-2 text-slate-600">{r.origin_country ? countryName(r.origin_country) : "Any origin"}</td>
                      <td className="px-3 py-2 text-slate-600">{r.category ? (labels[r.category] || r.category) : "Any"}</td>
                      <td className="px-3 py-2 font-mono text-slate-500">{r.hs_code || "—"}</td>
                      <td className="px-3 py-2 font-mono font-semibold text-[#0F172A]" data-testid={`rate-duty-${r.id}`}>{r.duty_pct}%</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{r.vat_pct}%</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{r.flat_fee || 0}</td>
                      <td className="max-w-[16rem] px-3 py-2 text-slate-500">{r.note || "—"}</td>
                      <td className="px-3 py-2">
                        {isAdmin && (
                          <button type="button" data-testid={`delete-rate-${r.id}`} onClick={async () => {
                            if (!window.confirm(`Remove the ${labels[r.category] || r.category || `HS ${r.hs_code}`} rate for ${countryName(r.dest_country) || r.dest_country}?`)) return;
                            try { await api.delete(`/tariffs/${r.id}`); toast.success("Rate removed"); await load(); }
                            catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                          }} className="text-[#B3241B] hover:underline">Remove</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a tariff rate</DialogTitle>
            <DialogDescription>Leave the origin as any origin to cover every country outside the destination. An HS code overrides the category.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Importing into</Label>
              <Select value={rate.dest_country} onValueChange={(v) => setRate({ ...rate, dest_country: v })}>
                <SelectTrigger data-testid="rate-dest-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Coming from</Label>
              <Select value={rate.origin_country} onValueChange={(v) => setRate({ ...rate, origin_country: v })}>
                <SelectTrigger data-testid="rate-origin-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="any">Any origin</SelectItem>
                  {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Product category</Label>
              <Select value={rate.category} onValueChange={(v) => setRate({ ...rate, category: v })}>
                <SelectTrigger data-testid="rate-category-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{categories.map((c) => <SelectItem key={c} value={c}>{labels[c] || c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>HS / tariff code (optional)</Label><Input data-testid="rate-hs-input" value={rate.hs_code} onChange={(e) => setRate({ ...rate, hs_code: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Duty %</Label><Input data-testid="rate-duty-input" type="number" min="0" max="100" value={rate.duty_pct} onChange={(e) => setRate({ ...rate, duty_pct: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Import VAT / GST %</Label><Input data-testid="rate-vat-input" type="number" min="0" max="100" value={rate.vat_pct} onChange={(e) => setRate({ ...rate, vat_pct: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Flat customs fee</Label><Input data-testid="rate-fee-input" type="number" min="0" value={rate.flat_fee} onChange={(e) => setRate({ ...rate, flat_fee: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>Note</Label><Input data-testid="rate-note-input" value={rate.note} onChange={(e) => setRate({ ...rate, note: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="save-rate-button" onClick={save} disabled={busy}>Save rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
